import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, OnInit, inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize } from 'rxjs';

import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { buildPaginationItems, buildPaginationLabel, buildQueryString, buildTimestampFileName, canShowPagination, downloadBlob, extractPagination, getApiErrorMessage } from '../../../helper/table-utils';
import { Httpservice } from '../../../services/httpservice';
import { Commonservice } from '../../../services/commonservice';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { ManageGeneratorAutocompleteComponent, ManageGeneratorAutocompleteCompleteEvent } from '../manage-generator/manage-generator-autocomplete';
import { MeterAddModalComponent } from './meter-add-modal';

type ModalMode = 'create' | 'edit' | 'view';
type SortField = 'generator_name' | 'meter_no' | 'mfr_name' | 'meter_type' | 'mf_value' | 'is_active';
type SortOrder = 'asc' | 'desc';

interface GeneratorOption { id: number; name: string; }
interface Meter {
  id: number;
  serial_no: number;
  gen_id: number;
  generator_name: string;
  meter_no: string;
  mfr_name: string;
  meter_type: string;
  mf_value: number | null;
  is_active: boolean | string | number | null;
}
interface Pagination { page_no: number; page_size: number; total_records: number; total_pages: number; }

const ALL = 'all';
const METER_TYPES = [{ label: 'Main', value: 'MAIN' }, { label: 'Check', value: 'CHECK' }];

@Component({
  selector: 'app-manage-meter-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, DashboardSidebarComponent, DashboardHeaderComponent, ManageGeneratorAutocompleteComponent, MeterAddModalComponent],
  templateUrl: './manage-meter.html',
  styleUrl: './manage-meter.scss',
})
export class ManageMeterPage implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly all = ALL;
  protected readonly meterTypes = METER_TYPES;
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly filterForm: UntypedFormGroup;
  protected readonly meterForm: UntypedFormGroup;
  protected readonly pageSizeControl: FormControl;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isExporting = false;
  protected isDownloadingTemplate = false;
  protected isImporting = false;
  protected actionMenuOpen = false;
  protected modalOpen = false;
  protected addMeterModalOpen = false;
  protected modalMode: ModalMode = 'create';
  protected formSubmitted = false;
  protected submitErrorMessage = '';
  protected selectedMeter: Meter | null = null;
  protected meters: Meter[] = [];
  protected generatorOptions: GeneratorOption[] = [];
  protected filteredGeneratorOptions: GeneratorOption[] = [];
  protected generatorSearchTerm = '';
  protected sortBy: SortField = 'meter_no';
  protected sortOrder: SortOrder = 'asc';
  protected pagination: Pagination = { page_no: 1, page_size: 15, total_records: 0, total_pages: 0 };

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host = inject(ElementRef<HTMLElement>);

  constructor(private readonly fb: UntypedFormBuilder, private readonly httpSvc: Httpservice, private readonly commonSvc: Commonservice) {
    this.filterForm = this.fb.group({ search: [''], generator_id: [ALL], page_size: [15] });
    this.pageSizeControl = this.filterForm.get('page_size') as FormControl;
    this.meterForm = this.fb.group({
      gen_id: [null, Validators.required],
      meter_no: ['', [Validators.required, Validators.pattern(/^\d+$/), Validators.maxLength(50)]],
      mfr_name: ['', Validators.maxLength(256)],
      meter_type: ['MAIN', Validators.required],
      mf_value: [null],
      is_active: [true],
    });
  }

  ngOnInit(): void {
    this.loadGeneratorOptions();
    this.loadMeters();
    this.filterForm.valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadMeters(1));
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) this.sidebarCollapsed = !this.sidebarCollapsed;
    else this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }
  protected closeMobileSidebar(): void { this.mobileSidebarOpen = false; }
  protected toggleActionMenu(): void { this.actionMenuOpen = !this.actionMenuOpen; }
  protected clearFilters(): void {
    this.filterForm.reset(
      { search: '', generator_id: ALL, page_size: this.pageSizeControl.value ?? 15 },
      { emitEvent: false },
    );
    this.actionMenuOpen = false;
    this.loadMeters(1);
  }

  protected openAddModal(): void {
    this.addMeterModalOpen = true;
  }
  protected closeAddMeterModal(): void { this.addMeterModalOpen = false; }
  protected onMeterCreated(): void { this.addMeterModalOpen = false; this.loadMeters(1); }
  protected openEditModal(meter: Meter): void {
    this.modalMode = 'edit'; this.selectedMeter = meter; this.formSubmitted = false; this.submitErrorMessage = '';
    this.meterForm.enable({ emitEvent: false });
    this.setGeneratorRequired(false);
    this.meterForm.reset({ gen_id: meter.gen_id, meter_no: meter.meter_no, mfr_name: meter.mfr_name === '-' ? '' : meter.mfr_name, meter_type: meter.meter_type.toUpperCase(), mf_value: meter.mf_value, is_active: meter.is_active });
    this.meterForm.get('meter_no')?.disable({ emitEvent: false });
    this.generatorSearchTerm = meter.generator_name; this.filterGenerators(this.generatorSearchTerm); this.modalOpen = true;
  }
  protected openViewModal(meter: Meter): void {
    this.modalMode = 'view'; this.selectedMeter = meter; this.formSubmitted = false; this.submitErrorMessage = '';
    this.meterForm.enable({ emitEvent: false });
    this.meterForm.reset({ gen_id: meter.gen_id, meter_no: meter.meter_no, mfr_name: meter.mfr_name === '-' ? '' : meter.mfr_name, meter_type: meter.meter_type.toUpperCase(), mf_value: meter.mf_value, is_active: meter.is_active });
    this.meterForm.disable({ emitEvent: false });
    this.generatorSearchTerm = meter.generator_name; this.filterGenerators(this.generatorSearchTerm); this.modalOpen = true;
  }
  protected closeModal(): void { this.modalOpen = false; this.meterForm.enable({ emitEvent: false }); this.setGeneratorRequired(true); }

  protected saveMeter(): void {
    this.formSubmitted = true;
    const selectedMeter = this.selectedMeter;
    const editing = this.modalMode === 'edit' && selectedMeter !== null;
    const generator = this.findGenerator(this.generatorSearchTerm);
    const generatorId = generator?.id ?? null;
    this.meterForm.patchValue({ gen_id: generatorId }, { emitEvent: false });
    if (this.meterForm.invalid || (!editing && !generator)) { this.submitErrorMessage = 'Please complete all required fields and select a generator.'; return; }
    const raw = this.meterForm.getRawValue();
    const payload = { ...raw, gen_id: generatorId };
    const requestPayload = editing && selectedMeter
      ? { ...payload, id: selectedMeter.id, meter_no: selectedMeter.meter_no }
      : payload;
    this.httpSvc.requestCall(editing ? AuthEndPoints.EDIT_METER : AuthEndPoints.ADD_METER, editing ? ApiMethod.PATCH : ApiMethod.POST, requestPayload, true)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: () => { this.closeModal(); this.loadMeters(this.pagination.page_no); }, error: error => this.submitErrorMessage = getApiErrorMessage(error) || 'Failed to save meter.' });
  }
  protected isActiveStatus(isActive: boolean | string | number | null | undefined): boolean {
    if (typeof isActive === 'string') {
      return isActive.trim().toLowerCase() === 'true' || isActive.trim() === '1';
    }
    return isActive === true || isActive === 1;
  }
  protected onMeterStatusChange(meter: Meter, active: boolean): void {
    const previous = this.isActiveStatus(meter.is_active);
    if (previous === active) return;
    meter.is_active = active;
    this.httpSvc.requestCall(AuthEndPoints.EDIT_METER, ApiMethod.PATCH, { id: meter.id, gen_id: meter.gen_id, meter_no: meter.meter_no, mfr_name: meter.mfr_name === '-' ? null : meter.mfr_name, meter_type: meter.meter_type.toUpperCase(), mf_value: meter.mf_value, is_active: active }, true)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: () => this.loadMeters(this.pagination.page_no), error: () => meter.is_active = previous });
  }

  protected onGeneratorSearch(event: ManageGeneratorAutocompleteCompleteEvent): void { this.filterGenerators(event.query); }
  protected onGeneratorBlur(): void { const generator = this.findGenerator(this.generatorSearchTerm); if (generator) { this.generatorSearchTerm = generator.name; this.meterForm.patchValue({ gen_id: generator.id }); } }
  protected get requiredGeneratorInvalid(): boolean { return this.modalMode !== 'edit' && this.formSubmitted && !this.findGenerator(this.generatorSearchTerm); }
  protected requiredInvalid(field: string): boolean { const control = this.meterForm.get(field); return Boolean(control?.invalid && (control.touched || this.formSubmitted)); }
  protected toggleSort(field: SortField): void { this.sortOrder = this.sortBy === field && this.sortOrder === 'asc' ? 'desc' : 'asc'; this.sortBy = field; this.loadMeters(1); }
  protected isSortAscending(field: SortField): boolean { return this.sortBy === field && this.sortOrder === 'asc'; }
  protected isSortDescending(field: SortField): boolean { return this.sortBy === field && this.sortOrder === 'desc'; }
  protected goToPreviousPage(): void { this.goToPage(this.pagination.page_no - 1); }
  protected goToNextPage(): void { this.goToPage(this.pagination.page_no + 1); }
  protected goToPage(page: number): void { if (page >= 1 && page <= this.pagination.total_pages) this.loadMeters(page); }
  protected canShowPagination(): boolean { return canShowPagination(this.pagination.total_records); }
  protected getPaginationLabel(): string { return buildPaginationLabel(this.pagination, 'No records', 'Showing', 'results'); }
  protected getPaginationItems(): number[] { return buildPaginationItems(this.pagination.total_pages, this.pagination.page_no); }
  protected trackByMeterId(_index: number, meter: Meter): number { return meter.id; }

  protected exportData(): void {
    this.actionMenuOpen = false; this.isExporting = true;
    this.httpSvc.downloadfile(AuthEndPoints.EXPORT_METERS, ApiMethod.GET, this.query({ page_no: undefined, page_size: undefined })).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => { this.isExporting = false; this.cdr.markForCheck(); })).subscribe({ next: blob => downloadBlob(blob, buildTimestampFileName('meters_export')), error: error => this.commonSvc.showErrorToastr(getApiErrorMessage(error) || 'Failed to export meters.') });
  }
  protected downloadTemplate(): void {
    this.actionMenuOpen = false; this.isDownloadingTemplate = true;
    this.httpSvc.downloadfile(AuthEndPoints.METER_TEMPLATE, ApiMethod.GET).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => { this.isDownloadingTemplate = false; this.cdr.markForCheck(); })).subscribe({ next: blob => downloadBlob(blob, 'meter_import_template.xlsx'), error: error => this.commonSvc.showErrorToastr(getApiErrorMessage(error) || 'Failed to download template.') });
  }
  protected triggerImport(input: HTMLInputElement): void { this.actionMenuOpen = false; input.click(); }
  protected importFile(event: Event): void {
    const input = event.target as HTMLInputElement; const file = input.files?.[0]; if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) { this.commonSvc.showErrorToastr('Only .xlsx files are allowed.'); input.value = ''; return; }
    this.isImporting = true; const data = new FormData(); data.append('file', file);
    this.httpSvc.requestCall(AuthEndPoints.IMPORT_METERS, ApiMethod.POST, data, true).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => { this.isImporting = false; input.value = ''; this.cdr.markForCheck(); })).subscribe({ next: () => this.loadMeters(1) });
  }

  @HostListener('document:click', ['$event']) protected closeActionMenu(event: Event): void { if (!this.host.nativeElement.contains(event.target as Node)) this.actionMenuOpen = false; }
  private loadGeneratorOptions(): void { this.httpSvc.requestCall(AuthEndPoints.GET_METER_GENERATORS, ApiMethod.GET, '').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: res => { this.generatorOptions = res?.value?.generators ?? []; this.filterGenerators(this.generatorSearchTerm); }, error: () => { this.generatorOptions = []; this.filteredGeneratorOptions = []; } }); }
  private setGeneratorRequired(required: boolean): void { const control = this.meterForm.get('gen_id'); control?.setValidators(required ? Validators.required : null); control?.updateValueAndValidity({ emitEvent: false }); }
  private filterGenerators(value: string): void { const term = value.trim().toLowerCase(); this.filteredGeneratorOptions = term ? this.generatorOptions.filter(item => item.name.toLowerCase().includes(term)) : [...this.generatorOptions]; }
  private findGenerator(value: string): GeneratorOption | undefined { const normalized = value.trim().toLowerCase(); return this.generatorOptions.find(item => item.name.trim().toLowerCase() === normalized); }
  private loadMeters(page = this.pagination.page_no): void {
    this.isLoading = true;
    this.httpSvc.requestCall(AuthEndPoints.GET_METERS_LIST, ApiMethod.GET, this.query({ page_no: page, page_size: this.pageSizeControl.value ?? 15 })).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => { this.isLoading = false; this.cdr.markForCheck(); })).subscribe({ next: res => { const value = res?.value ?? res ?? {}; this.meters = (value.meters ?? []).map((meter: Meter) => ({ ...meter, is_active: this.isActiveStatus(meter.is_active) })); this.pagination = extractPagination<Pagination>(value) ?? { page_no: page, page_size: this.pageSizeControl.value ?? 15, total_records: 0, total_pages: 0 }; }, error: () => { this.meters = []; } });
  }
  private query(extra: Record<string, string | number | undefined>) { return buildQueryString({ search: this.filterForm.get('search')?.value || undefined, generator_id: this.filterForm.get('generator_id')?.value === ALL ? undefined : this.filterForm.get('generator_id')?.value, sort_by: this.sortBy, sort_order: this.sortOrder, ...extra }); }
}
